import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import { formatTextContent } from '../../services/funnelService';

const Container = styled.div`
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  padding: ${theme.spacing.screenPadding};
  padding-top: calc(${theme.spacing.screenPadding} + 24px);
  padding-bottom: calc(${theme.spacing.screenPadding} + 20px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: 100vh;
    min-height: 100dvh;
    min-height: -webkit-fill-available;
    padding-bottom: calc(${theme.spacing.screenPadding} + 60px);
    overflow-y: scroll;
  }
  
  @media (max-width: 480px) {
    padding-bottom: calc(${theme.spacing.screenPadding} + 60px);
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: center;
  gap: ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.lg};
`;

const Title = styled.h2`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.lg};
`;

const Description = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  line-height: ${theme.typography.lineHeights.normal};
  margin-bottom: ${theme.spacing.lg};

  & strong, & b {
    font-weight: ${theme.typography.weights.semiBold} !important;
    color: ${theme.colors.text} !important;
  }

  & em, & i {
    font-style: italic !important;
  }
`;

const SummaryText = styled.div`
  text-align: center;
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.bodyLarge};
  line-height: ${theme.typography.lineHeights.normal};
  color: ${theme.colors.text};
  margin: ${theme.spacing.lg} 0;
  padding: 0 ${theme.spacing.md};
`;

const VisualizationContainer = styled.div`
  width: 100%;
  max-width: 80vw;
  margin: ${theme.spacing.xs} auto;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const VisualizationTitle = styled.h4`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h3};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.md};
  letter-spacing: ${theme.typography.letterSpacing.tight};
`;

const ChartContainer = styled.div`
  width: 80vw;
  max-width: 80vw;
  height: 180px;
  background: ${theme.colors.surface};
  border: 2px solid ${theme.colors.doryLighterBlue};
  border-radius: ${theme.borderRadius.lg};
  position: relative;
  overflow: hidden;
  margin: ${theme.spacing.md} auto;
  padding: 8px 12px;
  box-sizing: border-box;
`;

const DailyBarsContainer = styled.div`
  display: flex;
  align-items: flex-end;
  height: 100%;
  gap: 2px;
  padding: 30px 0 25px 0;
  position: relative;
`;

const BarWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  flex: 1;
  position: relative;
  height: 100%;
`;

const Bar = styled.div`
  width: 80%;
  background: ${props => props.color || theme.colors.primary};
  border-radius: 2px;
  position: relative;
  min-height: 4px;
  transition: height ${theme.animation.durations.normal};
`;

const BarLabel = styled.div`
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 10px;
  color: ${theme.colors.textLight};
  text-align: center;
  line-height: 1;
  white-space: nowrap;
  font-weight: ${theme.typography.weights.semiBold};
`;

const DateLabel = styled.div`
  position: absolute;
  bottom: -18px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 9px;
  color: ${theme.colors.textLight};
  text-align: center;
  line-height: 1;
  white-space: nowrap;
`;

const CompletionMessage = styled.div`
  background: ${theme.colors.success}10;
  border: 2px solid ${theme.colors.success};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  margin: ${theme.spacing.lg} 0;
  text-align: center;
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.bodyLarge};
  color: ${theme.colors.text};
  line-height: ${theme.typography.lineHeights.normal};
`;

const SprintRecoveryProtocolScreen = ({ data, onNext, userProgress = {} }) => {
  const [sprintPeriodDate, setSprintPeriodDate] = useState(null);
  const [sprintData, setSprintData] = useState(null);
  const [recoveryData, setRecoveryData] = useState(null);
  const [unifiedChartData, setUnifiedChartData] = useState(null);

  // Helper function to convert decimal hours to spelled-out hours and minutes
  const formatHoursAndMinutes = (decimalHours) => {
    if (decimalHours <= 0) return 'zero hours';
    
    const wholeHours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - wholeHours) * 60);
    
    const numberToWords = {
      0: 'zero', 1: 'one', 2: 'two', 3: 'three', 4: 'four', 5: 'five',
      6: 'six', 7: 'seven', 8: 'eight', 9: 'nine', 10: 'ten',
      11: 'eleven', 12: 'twelve', 13: 'thirteen', 14: 'fourteen', 15: 'fifteen',
      16: 'sixteen', 17: 'seventeen', 18: 'eighteen', 19: 'nineteen', 20: 'twenty',
      21: 'twenty-one', 22: 'twenty-two', 23: 'twenty-three', 24: 'twenty-four',
      25: 'twenty-five', 26: 'twenty-six', 27: 'twenty-seven', 28: 'twenty-eight',
      29: 'twenty-nine', 30: 'thirty', 31: 'thirty-one', 32: 'thirty-two',
      33: 'thirty-three', 34: 'thirty-four', 35: 'thirty-five', 36: 'thirty-six',
      37: 'thirty-seven', 38: 'thirty-eight', 39: 'thirty-nine', 40: 'forty',
      41: 'forty-one', 42: 'forty-two', 43: 'forty-three', 44: 'forty-four',
      45: 'forty-five', 46: 'forty-six', 47: 'forty-seven', 48: 'forty-eight',
      49: 'forty-nine', 50: 'fifty', 51: 'fifty-one', 52: 'fifty-two',
      53: 'fifty-three', 54: 'fifty-four', 55: 'fifty-five', 56: 'fifty-six',
      57: 'fifty-seven', 58: 'fifty-eight', 59: 'fifty-nine'
    };
    
    const hourNames = numberToWords;
    const minuteNames = numberToWords;
    
    if (wholeHours === 0) {
      return `${minuteNames[minutes] || minutes} minute${minutes !== 1 ? 's' : ''}`;
    } else if (minutes === 0) {
      return `${hourNames[wholeHours] || wholeHours} hour${wholeHours !== 1 ? 's' : ''}`;
    } else {
      return `${hourNames[wholeHours] || wholeHours} hour${wholeHours !== 1 ? 's' : ''} and ${minuteNames[minutes] || minutes} minute${minutes !== 1 ? 's' : ''}`;
    }
  };

  // Parse date from URL parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sprintDateParam = urlParams.get('sprint') || urlParams.get('sprintDate');
    
    if (sprintDateParam && /^\d{8}$/.test(sprintDateParam)) {
      // Parse YYYYMMDD format
      const year = parseInt(sprintDateParam.substring(0, 4));
      const month = parseInt(sprintDateParam.substring(4, 6)) - 1; // 0-indexed
      const day = parseInt(sprintDateParam.substring(6, 8));
      const date = new Date(year, month, day);
      setSprintPeriodDate(date);
    }
  }, []);

  // Calculate sprint and recovery data from journal entries
  useEffect(() => {
    if (!sprintPeriodDate || !userProgress.journalEntries || userProgress.journalEntries.length === 0) {
      return;
    }

    const entries = [...userProgress.journalEntries].sort((a, b) => 
      new Date(a.timestamp) - new Date(b.timestamp)
    );

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const sprintStart = new Date(sprintPeriodDate);
    sprintStart.setHours(0, 0, 0, 0);

    // Enhanced Optimal Pace Prediction System (same as User Lookup admin screen)
    const calculateEnhancedOptimalPace = (entries, targetIndex) => {
      if (!entries || entries.length === 0) return null;
      
      const targetEntry = entries[targetIndex];
      if (!targetEntry || !targetEntry.emotionalStates || !targetEntry.hoursWorked) return null;
      
      try {
        const historicalEntries = entries.slice(0, targetIndex + 1);
        const currentTime = new Date(targetEntry.timestamp);
        const lambda = 0.1; // Decay rate
        
        const weightedEntries = historicalEntries.map((entry, index) => {
          const entryTime = new Date(entry.timestamp);
          const daysDiff = (currentTime - entryTime) / (1000 * 60 * 60 * 24);
          const weight = Math.exp(-lambda * daysDiff);
          
          return { ...entry, weight, daysAgo: daysDiff };
        }).filter(entry => entry.weight > 0.01);
        
        if (weightedEntries.length === 0) return null;
        
        // Calculate weighted averages for emotional factors
        const emotionalFactors = ['mania', 'depression', 'energy', 'focus', 'calm', 'happiness', 'hours'];
        const weightedEmotionalAverages = {};
        
        emotionalFactors.forEach(factor => {
          const validEntries = weightedEntries.filter(entry => 
            entry.emotionalStates && 
            entry.emotionalStates[factor] !== null && 
            entry.emotionalStates[factor] !== undefined
          );
          
          if (validEntries.length > 0) {
            const weightedSum = validEntries.reduce((sum, entry) => 
              sum + (entry.emotionalStates[factor] * entry.weight), 0
            );
            const totalWeight = validEntries.reduce((sum, entry) => sum + entry.weight, 0);
            weightedEmotionalAverages[factor] = weightedSum / totalWeight;
          }
        });
        
        // Calculate weighted pressure average
        const pressureEntries = weightedEntries.filter(entry => 
          entry.pressureScore !== null && entry.pressureScore !== undefined
        );
        
        let weightedPressureAverage = 5;
        if (pressureEntries.length > 0) {
          const weightedPressureSum = pressureEntries.reduce((sum, entry) => 
            sum + (entry.pressureScore * entry.weight), 0
          );
          const totalPressureWeight = pressureEntries.reduce((sum, entry) => sum + entry.weight, 0);
          weightedPressureAverage = weightedPressureSum / totalPressureWeight;
        }
        
        // Calculate weighted hours worked average
        const hoursEntries = weightedEntries.filter(entry => 
          entry.hoursWorked !== null && entry.hoursWorked !== undefined
        );
        
        let weightedHoursAverage = 8;
        if (hoursEntries.length > 0) {
          const weightedHoursSum = hoursEntries.reduce((sum, entry) => 
            sum + (entry.hoursWorked * entry.weight), 0
          );
          const totalHoursWeight = hoursEntries.reduce((sum, entry) => sum + entry.weight, 0);
          weightedHoursAverage = weightedHoursSum / totalHoursWeight;
        }
        
        // Calculate current emotional arousal (inverted scale)
        const currentEmotionalValues = emotionalFactors
          .map(factor => weightedEmotionalAverages[factor])
          .filter(val => val !== undefined)
          .map(val => 10 - val);
        
        const currentArousal = currentEmotionalValues.length > 0 
          ? currentEmotionalValues.reduce((sum, val) => sum + val, 0) / currentEmotionalValues.length
          : 5;
        
        // Determine task complexity based on ACTUAL pressure score for this day
        const actualPressure = targetEntry.pressureScore;
        const isHighComplexity = actualPressure >= 6;
        
        // Calculate optimal arousal range based on actual pressure
        let optimalArousalRange;
        if (isHighComplexity) {
          optimalArousalRange = { min: 1.0, max: 3.0 };
        } else {
          optimalArousalRange = { min: 1.0, max: 5.0 };
        }
        
        // Calculate the middle of the optimal range as the target arousal
        const targetArousal = (optimalArousalRange.min + optimalArousalRange.max) / 2;
        
        // Calculate arousal ratio to move toward the middle of the optimal range
        const arousalRatio = targetArousal / Math.max(currentArousal, 0.1);
        
        // Apply pressure adjustment factor
        const pressureAdjustmentFactor = 1 + (weightedPressureAverage - 5) * 0.1;
        
        // Calculate optimal hours with pressure adjustment
        let optimalHours = weightedHoursAverage * arousalRatio * pressureAdjustmentFactor;
        
        // Apply caps
        const cappedArousalRatio = Math.max(0.5, Math.min(2.0, arousalRatio));
        optimalHours = Math.max(2, Math.min(18, optimalHours));
        
        return Math.round(optimalHours * 10) / 10;
        
      } catch (error) {
        console.error('Error calculating enhanced optimal pace:', error);
        return null;
      }
    };

    // Step 1: Build a map of date -> optimal pace (using Enhanced Optimal Pace calculation)
    // Entry on Oct 7 provides Optimal Pace for Oct 7 (calculated with historical context up to Oct 7)
    const optimalPaceByDate = new Map();
    console.log('\n=== Processing Journal Entries for Optimal Pace ===');
    entries.forEach((entry, index) => {
      const entryDate = new Date(entry.timestamp);
      entryDate.setHours(0, 0, 0, 0);
      const dateKey = entryDate.toISOString().split('T')[0];
      
      // Use Enhanced Optimal Pace calculation (same as User Lookup admin screen)
      const optimalHours = calculateEnhancedOptimalPace(entries, index) || 8;
      optimalPaceByDate.set(dateKey, optimalHours);
      console.log(`Entry ${dateKey}: Enhanced Optimal Pace = ${optimalHours.toFixed(2)}h`);
    });

    // Step 2: Build a map of date -> hours worked (from next day's entry)
    // Entry on Oct 3 provides Hours Worked for Oct 2
    const hoursWorkedByDate = new Map();
    console.log('\n=== Processing Journal Entries for Hours Worked ===');
    entries.forEach(entry => {
      const entryDate = new Date(entry.timestamp);
      entryDate.setHours(0, 0, 0, 0);
      
      // hoursWorked refers to the day BEFORE the entry date
      const workDate = new Date(entryDate);
      workDate.setDate(workDate.getDate() - 1);
      workDate.setHours(0, 0, 0, 0);
      const dateKey = workDate.toISOString().split('T')[0];
      const entryDateKey = entryDate.toISOString().split('T')[0];
      
      hoursWorkedByDate.set(dateKey, entry.hoursWorked || 0);
      console.log(`Entry ${entryDateKey}: hoursWorked = ${(entry.hoursWorked || 0).toFixed(2)}h (for work date ${dateKey})`);
    });

    // Debug logging - show all dates
    console.log('=== SPRINT RECOVERY DEBUG START ===');
    console.log('Sprint Start Date:', sprintStart.toISOString().split('T')[0]);
    console.log('Today:', today.toISOString().split('T')[0]);
    console.log('\nOptimal Pace by Date:');
    Array.from(optimalPaceByDate.keys()).sort().forEach(key => {
      console.log(`  ${key}: ${optimalPaceByDate.get(key).toFixed(2)}h`);
    });
    console.log('\nHours Worked by Date:');
    Array.from(hoursWorkedByDate.keys()).sort().forEach(key => {
      console.log(`  ${key}: ${hoursWorkedByDate.get(key).toFixed(2)}h`);
    });

    // Step 3: For each day, match Optimal Pace with Hours Worked
    const sprintEntries = [];
    const recoveryEntries = [];
    let lastSprintDate = null;

    // Get all dates that have both optimal pace and hours worked data
    const allDates = new Set([...optimalPaceByDate.keys(), ...hoursWorkedByDate.keys()]);
    
    // Sort dates and process each one
    console.log('\n=== Processing Date Range ===');
    Array.from(allDates)
      .sort()
      .forEach(dateKey => {
        // Parse date string directly to avoid timezone issues
        const [year, month, day] = dateKey.split('-').map(Number);
        const date = new Date(year, month - 1, day); // month is 0-indexed
        date.setHours(0, 0, 0, 0);
        
        console.log(`Checking ${dateKey}: sprintStart=${sprintStart.toISOString().split('T')[0]}, date=${date.toISOString().split('T')[0]}, today=${today.toISOString().split('T')[0]}`);
        console.log(`  date >= sprintStart: ${date >= sprintStart}, date <= today: ${date <= today}`);
        
        // Only process dates in our range
        if (date >= sprintStart && date <= today) {
          const optimalHours = optimalPaceByDate.get(dateKey);
          const hoursWorked = hoursWorkedByDate.get(dateKey);
          
          // Only calculate deficit if we have both values for this date
          if (optimalHours !== undefined && hoursWorked !== undefined) {
            const difference = hoursWorked - optimalHours;
            
            // Debug logging for each day
            console.log(`\n${dateKey} Calculation:`);
            console.log(`  Optimal Pace: ${optimalHours.toFixed(2)}h`);
            console.log(`  Hours Worked: ${hoursWorked.toFixed(2)}h`);
            console.log(`  Difference: ${difference.toFixed(2)}h`);
            console.log(`  Is Sprint Day: ${difference > 0}`);
            
            // Only include days where Hours Worked > Optimal Pace (sprint days)
            // Each such day will get a bar in the Sprint Deficit Accumulation chart
            if (difference > 0) {
              // Sprint day: worked above optimal pace
              sprintEntries.push({
                date: date,
                dateKey: dateKey,  // Store the ISO date string for accurate formatting
                hoursWorked,
                optimalHours,
                deficit: difference  // Hours worked above optimal pace
              });
              lastSprintDate = date;
            } else if (lastSprintDate && date > lastSprintDate && difference < 0) {
              // Recovery day: worked below optimal pace, after sprint period ended
              recoveryEntries.push({
                date: date,
                dateKey: dateKey,  // Store the ISO date string for accurate formatting
                hoursWorked,
                optimalHours,
                recovery: Math.abs(difference)  // Hours worked below optimal pace
              });
            }
          }
        }
      });

    // Calculate cumulative deficit
    let cumulativeDeficit = 0;
    const sprintDataWithCumulative = sprintEntries.map(day => {
      cumulativeDeficit += day.deficit;
      return {
        ...day,
        cumulativeDeficit
      };
    });

    // Calculate deficit burn-down
    const totalDeficit = cumulativeDeficit;
    let remainingDeficit = totalDeficit;
    const recoveryDataWithBurndown = recoveryEntries.map(day => {
      remainingDeficit = Math.max(0, remainingDeficit - day.recovery);
      return {
        ...day,
        remainingDeficit
      };
    });

    setSprintData({
      days: sprintDataWithCumulative,
      totalDays: sprintEntries.length,
      totalDeficit
    });

    setRecoveryData({
      days: recoveryDataWithBurndown,
      totalDays: recoveryEntries.length,
      totalRecovery: recoveryEntries.reduce((sum, day) => sum + day.recovery, 0),
      remainingDeficit: remainingDeficit
    });

    // Create unified chart data combining sprint and recovery
    const allChartData = [];

    // Add sprint days (where difference > 0)
    sprintDataWithCumulative.forEach(day => {
      allChartData.push({
        date: day.date,
        dateKey: day.dateKey,
        hoursWorked: day.hoursWorked,
        optimalHours: day.optimalHours,
        difference: day.deficit,
        isSprintDay: true
      });
    });

    // Add recovery days (where difference < 0)
    recoveryDataWithBurndown.forEach(day => {
      allChartData.push({
        date: day.date,
        dateKey: day.dateKey,
        hoursWorked: day.hoursWorked,
        optimalHours: day.optimalHours,
        difference: -day.recovery, // Negative value for recovery
        isSprintDay: false
      });
    });

    // Sort all data by date to ensure chronological order
    allChartData.sort((a, b) => new Date(a.dateKey) - new Date(b.dateKey));

    // Calculate rolling deficit based on daily differences, never allowing it to go below zero
    let runningDeficit = 0;
    allChartData.forEach(day => {
      runningDeficit = Math.max(0, runningDeficit + day.difference);
      day.cumulativeDeficit = runningDeficit;
    });

    setUnifiedChartData(allChartData);

  }, [sprintPeriodDate, userProgress.journalEntries]);

  // Calculate estimated days remaining
  const calculateEstimatedDaysRemaining = () => {
    if (!recoveryData || recoveryData.days.length === 0 || recoveryData.remainingDeficit <= 0) {
      return 0;
    }

    // Use the rounded average recovery per day (same as displayed in text)
    const averageRecoveryPerDay = Math.round(recoveryData.totalRecovery / recoveryData.totalDays);
    if (averageRecoveryPerDay <= 0) {
      return Infinity;
    }

    return Math.ceil(recoveryData.remainingDeficit / averageRecoveryPerDay);
  };

  // Format date as MM/DD
  const formatDate = (dateStr) => {
    // Parse the ISO date string (YYYY-MM-DD) directly to avoid timezone issues
    const [year, month, day] = dateStr.split('-').map(Number);
    return `${month}/${day}`;
  };

  const htmlContent = formatTextContent(data.content?.text, userProgress);

  const isRecoveryComplete = recoveryData && recoveryData.remainingDeficit <= 0;
  const estimatedDaysRemaining = calculateEstimatedDaysRemaining();

  return (
    <Container>
      <Content>
        {sprintPeriodDate && (
          <Title style={{ marginBottom: '0px' }}>
            {sprintPeriodDate.toLocaleDateString('en-US', { 
              month: 'long', 
              day: 'numeric'
            })}th Sprint Tracking and Recovery
          </Title>
        )}
        
        {htmlContent && (
          <Description dangerouslySetInnerHTML={{ __html: htmlContent }} />
        )}

        {!sprintPeriodDate && (
          <Description style={{ marginBottom: '20px' }}>
            <strong>Error:</strong> No sprint date provided. Please add a sprint date to the URL using the format: ?sprint=YYYYMMDD
          </Description>
        )}

        {sprintPeriodDate && sprintData && (
          <>
            {/* Sprint Summary */}
            <SummaryText style={{ marginTop: '12px', marginBottom: '12px' }}>
              You sprinted for <strong>{unifiedChartData ? unifiedChartData.filter(day => day.difference > 0).length : sprintData.totalDays}</strong> {unifiedChartData && unifiedChartData.filter(day => day.difference > 0).length === 1 ? 'day' : 'days'} and worked <strong>{unifiedChartData ? Math.round(Math.max(...unifiedChartData.map(day => day.cumulativeDeficit))) : Math.round(sprintData.totalDeficit)} hours</strong> above your Optimal Pace at the peak of the Sprint.
            </SummaryText>

            {/* Unified Sprint Deficit Accumulation and Burn-down Chart */}
            {unifiedChartData && unifiedChartData.length > 0 && (
              <VisualizationContainer>
                <VisualizationTitle>Sprint Deficit Accumulation and Burn-down</VisualizationTitle>
                
                <ChartContainer>
                  <DailyBarsContainer>
                    {unifiedChartData.map((day, index) => {
                      const maxDeficit = sprintData.totalDeficit;
                      const heightPercentage = maxDeficit > 0 ? (day.cumulativeDeficit / maxDeficit) * 100 : 0;
                      
                      return (
                        <BarWrapper key={index}>
                          <Bar 
                            style={{ 
                              height: `${Math.max(4, heightPercentage)}%`,
                              background: day.difference < 0 ? theme.colors.doryLighterBlue : theme.colors.primary
                            }}
                          >
                            <BarLabel>{Math.round(day.cumulativeDeficit)}</BarLabel>
                          </Bar>
                          <DateLabel>{formatDate(day.dateKey)}</DateLabel>
                        </BarWrapper>
                      );
                    })}
                  </DailyBarsContainer>
                </ChartContainer>
                
                {/* Chart Legend */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  gap: '20px', 
                  marginTop: '16px',
                  flexWrap: 'wrap'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      backgroundColor: theme.colors.primary,
                      borderRadius: '2px'
                    }}></div>
                    <span style={{ 
                      fontSize: '14px', 
                      color: theme.colors.text,
                      fontFamily: theme.typography.bodyFont
                    }}>Sprint Days</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      backgroundColor: theme.colors.doryLighterBlue,
                      borderRadius: '2px'
                    }}></div>
                    <span style={{ 
                      fontSize: '14px', 
                      color: theme.colors.text,
                      fontFamily: theme.typography.bodyFont
                    }}>Recovery Days</span>
                  </div>
                </div>
              </VisualizationContainer>
            )}

            {/* Recovery Section */}
            {recoveryData && recoveryData.days.length > 0 && (
              <>

                {/* Completion or Estimation Message */}
                {isRecoveryComplete ? (
                  <CompletionMessage style={{ marginTop: '0px', marginBottom: '20px', paddingTop: '10px' }}>
                    You worked below your Optimal Pace for <strong>{recoveryData.totalDays}</strong> {recoveryData.totalDays === 1 ? 'day' : 'days'} and fully paid back your Sprint deficit of <strong>{formatHoursAndMinutes(Math.round(sprintData.totalDeficit))}</strong>. Your Sprint recovery period is complete.
                  </CompletionMessage>
                ) : (
                  <SummaryText style={{ marginTop: '0px', marginBottom: '20px', paddingTop: '10px' }}>
                    If you keep working <strong>{Math.round(recoveryData.totalRecovery / recoveryData.totalDays)}</strong> fewer {Math.round(recoveryData.totalRecovery / recoveryData.totalDays) === 1 ? 'hour' : 'hours'} than your Optimal Pace each day, you need approximately <strong>{estimatedDaysRemaining}</strong> more {estimatedDaysRemaining === 1 ? 'day' : 'days'} to fully recover from your Sprint.
                  </SummaryText>
                )}
              </>
            )}

            {(!recoveryData || recoveryData.days.length === 0) && (
              <SummaryText style={{ marginTop: '0px', marginBottom: '20px', paddingTop: '10px' }}>
                Start working fewer hours than your Optimal Pace to end your Sprint and begin your Recovery Period. We'll track your progress and show a burn-down chart once you have at least one recovery day.
              </SummaryText>
            )}
          </>
        )}

        {sprintPeriodDate && !sprintData && (
          <Description style={{ marginTop: '0px', marginBottom: '20px', paddingTop: '10px' }}>
            No Sprint data found starting from {sprintPeriodDate.toLocaleDateString()}. Make sure you have check-ins during the Sprint period.
          </Description>
        )}
      </Content>
    </Container>
  );
};

export default SprintRecoveryProtocolScreen;

